import { Component, inject, Input, OnInit, signal } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { faEdit, faPlus, faTrash, faTimes, faSave } from '@fortawesome/free-solid-svg-icons'
import { AgGridAngular } from 'ag-grid-angular'
import { ColDef, GridApi, GridReadyEvent } from 'ag-grid-community'
import { AG_GRID_LOCALE_DE } from '../../../_helpers/aggrid.locale.de'
import { AgGridThemeService } from '../../../_services/ag-grid-theme.service'
import {
  AgGridFAActionSymbolRenderer,
  FaActionIcon
} from '../../_shared/ag-grid-renderer/ag-grid-fa-action-symbols.component'
import { DismantlingArticleService } from '../../../_services/dismantling-article.service'
import { ArticleService } from '../../../_services'
import { ArticleTemplate } from '../../../_models'
import {
  DismantlingArticle,
  DismantlingArticleDisposition,
  CreateDismantlingArticleParams,
  UnclearDismantlingRecord
} from '../../../_models/dismantling-article.interface'
import { environment } from '../../../../environments/environment'

@Component({
  selector: 'app-dismantling-articles-tab',
  standalone: true,
  imports: [
    FormsModule,
    FaIconComponent,
    AgGridAngular,
  ],
  host: { class: 'flex flex-col h-full' },
  templateUrl: './dismantling-articles-tab.component.html',
})
export class DismantlingArticlesTabComponent implements OnInit {

  @Input() projectId!: number

  // Services
  private dismantlingService = inject(DismantlingArticleService)
  private articleService = inject(ArticleService)

  // Icons
  protected readonly faPlus = faPlus
  protected readonly faTimes = faTimes
  protected readonly faSave = faSave

  // AG-Grid
  protected readonly locale = AG_GRID_LOCALE_DE
  protected hawkTheme = inject(AgGridThemeService).getTheme()
  private articlesGridApi?: GridApi
  private unclearGridApi?: GridApi

  // Artikel-Formular State
  showForm = signal<boolean>(false)
  editingArticleId = signal<number | null>(null)
  formDescription = ''
  formDisposition: DismantlingArticleDisposition | '' = ''
  formDisposalDescription = ''
  formArticleTemplateId: number | null = null
  formCaptureSns = false

  // Artikelvorlage-Suche
  articleTemplateSearch = ''
  articleTemplates = signal<ArticleTemplate[]>([])
  filteredTemplates = signal<ArticleTemplate[]>([])
  showTemplateDropdown = signal<boolean>(false)
  selectedTemplateName = ''
  templateRequiresSn = false

  // Zuweisungs-State fuer Klaerfaelle
  assigningRecordId = signal<number | null>(null)
  selectedArticleIdForAssign: number | null = null

  // Bilder-Popup
  showImagePopup = signal<boolean>(false)
  imagePopupUrls = signal<string[]>([])
  imagePopupTitle = signal<string>('')

  // === AG-Grid Column Definitions ===

  articlesColDefs: ColDef[] = [
    {
      field: 'description',
      headerName: 'Beschreibung',
      flex: 2,
    },
    {
      field: 'default_disposition',
      headerName: 'Disposition',
      width: 140,
      cellRenderer: (params: any) => {
        const val = params.value
        const labels: Record<string, { text: string; css: string }> = {
          disposal: { text: 'Entsorgung', css: 'bm-badge bm-badge-error' },
          storage: { text: 'Einlagerung', css: 'bm-badge bm-badge-success' },
          return: { text: 'Rücksendung', css: 'bm-badge bm-badge-info' },
        }
        const info = labels[val]
        return info ? `<span class="${info.css}">${info.text}</span>` : ''
      },
    },
    {
      field: 'article_template_description',
      headerName: 'Artikelvorlage',
      flex: 1,
      valueFormatter: (params: any) => params.value || '-',
    },
    {
      field: 'disposal_description',
      headerName: 'Entsorgungshinweis',
      flex: 1,
      valueFormatter: (params: any) => params.value || '-',
    },
    {
      field: 'capture_sns',
      headerName: 'SN',
      width: 70,
      cellRenderer: (params: any) => params.value ? '<span class="bm-badge bm-badge-neutral bm-badge-xs">SN</span>' : '',
    },
    {
      field: 'actions',
      headerName: '',
      width: 100,
      sortable: false,
      filter: false,
      cellRenderer: AgGridFAActionSymbolRenderer,
      valueGetter: (params: any): FaActionIcon[] => [
        {
          icon: faEdit,
          action: () => this.openEditForm(params.data),
          css_classes: '',
          tooltip: 'Bearbeiten',
        },
        {
          icon: faTrash,
          action: () => this.deleteArticle(params.data),
          css_classes: '',
          tooltip: 'Löschen',
        },
      ],
    },
  ]

  unclearColDefs: ColDef[] = [
    {
      field: 'free_text_description',
      headerName: 'Beschreibung',
      flex: 2,
      valueFormatter: (params: any) => params.value || 'Unbekannter Artikel',
    },
    {
      field: 'quantity',
      headerName: 'Menge',
      width: 80,
    },
    {
      field: 'ticket_order_nr',
      headerName: 'Ticket',
      width: 130,
      valueFormatter: (params: any) => params.value || '-',
    },
    {
      field: 'serial_numbers',
      headerName: 'Seriennummern',
      flex: 1,
      valueFormatter: (params: any) => {
        const sns = params.value as string[]
        return sns?.length ? sns.join(', ') : '-'
      },
    },
    {
      field: 'image_filenames',
      headerName: 'Bilder',
      width: 100,
      cellRenderer: (params: any) => {
        const filenames = params.value as string[]
        const count = filenames?.length || 0
        if (count === 0) return '-'
        return `<span class="bm-btn bm-btn-ghost bm-btn-xs">${count} Bild${count > 1 ? 'er' : ''}</span>`
      },
      onCellClicked: (params: any) => {
        const record = params.data as UnclearDismantlingRecord
        const filenames = record.image_filenames
        if (filenames?.length > 0) {
          this.openImagePopup(record.id, filenames, record.free_text_description || 'Klärfall')
        }
      },
    },
    {
      field: 'processed_by_name',
      headerName: 'Bearbeiter',
      width: 130,
    },
    {
      field: 'created_at',
      headerName: 'Datum',
      width: 140,
      valueFormatter: (params: any) => {
        if (!params.value) return ''
        const d = new Date(params.value)
        return d.toLocaleDateString('de-DE') + ' ' + d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
      },
    },
    {
      field: 'dismantling_article_description',
      headerName: 'Zugewiesen',
      flex: 1,
      cellRenderer: (params: any) => {
        if (params.data.dismantling_article_id) {
          return `<span class="bm-badge bm-badge-success">${params.value}</span>`
        }
        return `<button class="bm-btn bm-btn-sm bm-btn-ghost">Zuweisen</button>`
      },
      onCellClicked: (params: any) => {
        const record = params.data as UnclearDismantlingRecord
        if (!record.dismantling_article_id) {
          this.startAssign(record)
        }
      },
    },
  ]

  defaultColDef: ColDef = {
    filter: true,
    sortable: true,
    resizable: true,
  }

  ngOnInit() {
    this.dismantlingService.loadArticles(this.projectId)
    this.dismantlingService.loadUnclearRecords(this.projectId)
    this.loadArticleTemplates()
  }

  // Getter fuer Template-Zugriff auf Signals
  get articles() {
    return this.dismantlingService.articles()
  }

  get unclearRecords() {
    return this.dismantlingService.unclearRecords()
  }

  get isLoading() {
    return this.dismantlingService.isLoading()
  }

  // === Bilder-Popup ===

  openImagePopup(recordId: number, filenames: string[], title: string) {
    const urls = filenames.map(f => `${environment.apiURL}dismantling_records/${recordId}/images/${f}`)
    this.imagePopupUrls.set(urls)
    this.imagePopupTitle.set(title)
    this.showImagePopup.set(true)
  }

  closeImagePopup() {
    this.showImagePopup.set(false)
    this.imagePopupUrls.set([])
  }

  // === AG-Grid Events ===

  onArticlesGridReady(params: GridReadyEvent) {
    this.articlesGridApi = params.api
  }

  onUnclearGridReady(params: GridReadyEvent) {
    this.unclearGridApi = params.api
  }

  onUnclearRowClicked(event: any) {
    const record = event.data as UnclearDismantlingRecord
    if (!record.dismantling_article_id) {
      this.startAssign(record)
    }
  }

  // === Artikel-CRUD ===

  openNewForm() {
    this.resetForm()
    this.showForm.set(true)
    this.editingArticleId.set(null)
  }

  openEditForm(article: DismantlingArticle) {
    this.formDescription = article.description
    this.formDisposition = article.default_disposition || ''
    this.formDisposalDescription = article.disposal_description || ''
    this.formArticleTemplateId = article.article_template_id || null
    this.formCaptureSns = article.capture_sns
    this.selectedTemplateName = article.article_template_description || ''
    this.articleTemplateSearch = article.article_template_description || ''
    this.showForm.set(true)
    this.editingArticleId.set(article.id)
  }

  cancelForm() {
    this.resetForm()
    this.showForm.set(false)
    this.editingArticleId.set(null)
  }

  canSaveArticle(): boolean {
    if (!this.formDisposition) return false
    if (this.formDisposition === 'storage') {
      return !!this.formArticleTemplateId
    }
    return !!this.formDescription.trim()
  }

  saveArticle() {
    if (!this.canSaveArticle()) return

    // Bei Einlagerung: Beschreibung automatisch aus Artikelvorlage übernehmen
    const description = this.formDisposition === 'storage'
      ? this.selectedTemplateName
      : this.formDescription

    const params: CreateDismantlingArticleParams = {
      description: description,
      default_disposition: this.formDisposition || undefined,
      article_template_id: this.formDisposition === 'storage' ? this.formArticleTemplateId || undefined : undefined,
      disposal_description: this.formDisposition === 'disposal' ? this.formDisposalDescription || undefined : undefined,
      capture_sns: this.formCaptureSns,
    }

    const editId = this.editingArticleId()
    if (editId) {
      this.dismantlingService.updateArticle(this.projectId, editId, params)
    } else {
      this.dismantlingService.createArticle(this.projectId, params)
    }

    this.cancelForm()
  }

  deleteArticle(article: DismantlingArticle) {
    if (confirm(`Rückbau-Artikel "${article.description}" wirklich löschen?`)) {
      this.dismantlingService.deleteArticle(this.projectId, article.id)
    }
  }

  // === Artikelvorlage-Suche ===

  loadArticleTemplates() {
    this.articleService.loadArticleTemplates().subscribe({
      next: (templates) => {
        this.articleTemplates.set(templates)
      }
    })
  }

  onTemplateSearchInput() {
    const term = this.articleTemplateSearch.toLowerCase()
    if (term.length < 2) {
      this.filteredTemplates.set([])
      this.showTemplateDropdown.set(false)
      return
    }
    const filtered = this.articleTemplates().filter(t =>
      t.description?.toLowerCase().includes(term)
    )
    this.filteredTemplates.set(filtered.slice(0, 20))
    this.showTemplateDropdown.set(true)
  }

  selectTemplate(template: ArticleTemplate) {
    this.formArticleTemplateId = template.id
    this.articleTemplateSearch = template.description
    this.selectedTemplateName = template.description
    this.templateRequiresSn = !!template.sn
    if (this.templateRequiresSn) {
      this.formCaptureSns = true
    }
    this.showTemplateDropdown.set(false)
  }

  clearTemplate() {
    this.formArticleTemplateId = null
    this.articleTemplateSearch = ''
    this.selectedTemplateName = ''
    this.templateRequiresSn = false
    this.formCaptureSns = false
  }

  // === Klaerfaelle ===

  startAssign(record: UnclearDismantlingRecord) {
    this.assigningRecordId.set(record.id)
    this.selectedArticleIdForAssign = null
  }

  cancelAssign() {
    this.assigningRecordId.set(null)
    this.selectedArticleIdForAssign = null
  }

  confirmAssign(recordId: number) {
    if (!this.selectedArticleIdForAssign) return
    this.dismantlingService.assignArticleToRecord(recordId, this.selectedArticleIdForAssign)
    this.cancelAssign()
  }

  getImageUrl(recordId: number, filename: string): string {
    return this.dismantlingService.getImageUrl(recordId, filename)
  }

  // === Hilfsmethoden ===

  private resetForm() {
    this.formDescription = ''
    this.formDisposition = ''
    this.formDisposalDescription = ''
    this.formArticleTemplateId = null
    this.formCaptureSns = false
    this.templateRequiresSn = false
    this.articleTemplateSearch = ''
    this.selectedTemplateName = ''
    this.showTemplateDropdown.set(false)
  }
}
